import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {collection, onSnapshot} from 'firebase/firestore';
import sortBy from 'lodash/sortBy';

import {db} from '-/firebase';
import {hasPermission, processRawDocs} from '-/data/utils';
import {UserContext} from '-/contexts/User';
import {buildLocationIndex, locationPath as computeLocationPath} from '-/pages/gear/redesign/locationValue';

// Managed non-apparatus gear locations (#1405) — a storage locker, the hall, a
// shelf. Apparatus is the OTHER kind of location; see
// `pages/gear/redesign/locationValue.js`. Seeded from the tenant's stations on
// new-tenant bootstrap. Each doc: {name, parentId, archived} — `parentId` is
// present (null while flat) from day one so Phase 2 nesting needs no migration.
//
// Mirrors the Apparatus context: a tiny collection, gated on auth, provided
// globally so every gear enrichment site (ItemsContext, ItemDrawerRoute,
// GearItem, GearItemSummary — several of which render outside the gear
// workspace shell) can resolve a location value without threading a provider.

export const GearLocationsContext = createContext({
    gearLocations: [],
    byId: new Map(),
    childrenById: new Map(),
    roots: [],
    locationPath: () => [],
    loading: true
});

export function GearLocationsProvider({children}) {
    const {currentUser} = useContext(UserContext);
    const [gearLocations, setGearLocations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Requires auth AND gear.read (same as apparatus) — subscribing before
    // onAuthStateChanged resolves hits permission-denied and leaves the listener
    // dead, and subscribing without the permission hits it on every page load
    // forever. `gear.read` is default-true on the client (DefaultPermissions) but
    // the rules only see the custom claim, where a restricted member carries an
    // explicit `ge.r: false` (NORTHSHOREFD-6970A-6).
    useEffect(() => {
        if (!currentUser || !hasPermission(currentUser, 'gear.read')) {
            setGearLocations([]);
            setLoading(false);
            return undefined;
        }

        const ref = collection(db, 'gearLocations');
        const unsubscribe = onSnapshot(
            ref,
            snapshot => {
                setGearLocations(sortBy(processRawDocs(snapshot), 'name'));
                setLoading(false);
            },
            error => {
                console.warn('Gear locations snapshot error:', error);
                setLoading(false);
            }
        );
        return () => unsubscribe();
    }, [currentUser]);

    // Tree index (Phase 2): parentId forms a self-referential hierarchy. Live
    // (non-archived) docs only, so an archived parent doesn't keep its subtree in
    // the picker/breadcrumbs.
    const index = useMemo(() => buildLocationIndex(gearLocations.filter(l => !l.archived)), [gearLocations]);

    const value = useMemo(() => ({
        gearLocations,
        byId: index.byId,
        childrenById: index.childrenById,
        roots: index.roots,
        locationPath: uid => computeLocationPath(uid, index.byId),
        loading
    }), [gearLocations, index, loading]);

    return (
        <GearLocationsContext.Provider value={value}>
            {children}
        </GearLocationsContext.Provider>
    );
}

export const useGearLocations = () => useContext(GearLocationsContext);
